import { AddressDisplay, Button, Glass, Skeleton } from "@gotts.ai/ui";

import { AnimatedBalance } from "../../components/AnimatedBalance";
import { CompositionBar } from "../../components/CompositionBar";
import { TokenIcon } from "../../components/TokenIcon";
import { useDevenvNavigate } from "../../hooks/useDevenvNavigate";
import { DEVENV_TOKEN_PRICES } from "../../lib/constants";
import { formatUsd } from "../../lib/format";

import { computeTvlUsd, getDecimals } from "./types";

import type { V2PoolState } from "./types";

export function V2PoolCard({
  pair,
  tokenAddresses,
  state,
  tokenSymbols,
}: {
  pair: string;
  tokenAddresses: [string, string] | null;
  state: V2PoolState | null;
  tokenSymbols: readonly [string, string];
}) {
  const navigate = useDevenvNavigate();

  const d0 = getDecimals(tokenSymbols[0]);
  const d1 = getDecimals(tokenSymbols[1]);
  const r0 = state ? Number(state.reserve0) / 10 ** d0 : 0;
  const r1 = state ? Number(state.reserve1) / 10 ** d1 : 0;

  const tvl = state
    ? computeTvlUsd(
        [
          { symbol: tokenSymbols[0], amount: r0 },
          { symbol: tokenSymbols[1], amount: r1 },
        ],
        DEVENV_TOKEN_PRICES,
      )
    : 0;

  const price = state && r0 > 0 ? r1 / r0 : 0;
  const priceAnomaly = price > 1e12;

  return (
    <Glass className="glass-card-interactive cursor-pointer p-4">
      {/* Header: token pair + fee badge */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1.5">
            <TokenIcon symbol={tokenSymbols[0]} size={24} />
            <TokenIcon symbol={tokenSymbols[1]} size={24} />
          </div>
          <div>
            <span className="text-gold font-mono text-sm font-semibold">
              {pair}
            </span>
            <div className="pool-card-fee-pill">0.3%</div>
          </div>
        </div>
      </div>

      {state ? (
        <div className="space-y-3">
          {/* Price display */}
          <div className="text-text-muted text-xs">
            Price:{" "}
            <span className="text-text-dim font-mono">
              {priceAnomaly ? (
                <span className="text-amber-400">Price anomaly</span>
              ) : r0 === 0 ? (
                "N/A"
              ) : (
                <>
                  1 {tokenSymbols[0].toUpperCase()} ={" "}
                  <AnimatedBalance value={price} decimals={4} emDash={false} />{" "}
                  {tokenSymbols[1].toUpperCase()}
                </>
              )}
            </span>
          </div>

          {/* Reserves */}
          <div className="text-text-muted text-xs">
            Reserve {tokenSymbols[0].toUpperCase()}:{" "}
            <AnimatedBalance value={r0} className="text-text-dim" />
          </div>
          <div className="text-text-muted text-xs">
            Reserve {tokenSymbols[1].toUpperCase()}:{" "}
            <AnimatedBalance value={r1} className="text-text-dim" />
          </div>

          {(r0 === 0 || r1 === 0) && r0 + r1 > 0 && (
            <div className="bg-amber-400/[0.06] px-2 py-1 text-center text-[11px] text-amber-400">
              Fully one-sided
            </div>
          )}

          {/* Composition bar + TVL */}
          <CompositionBar
            value0={r0}
            value1={r1}
            label0={tokenSymbols[0].toUpperCase()}
            label1={tokenSymbols[1].toUpperCase()}
            height={6}
            tvlLabel={tvl > 0 ? `TVL: ${formatUsd(tvl)}` : undefined}
          />
        </div>
      ) : (
        <div className="space-y-2">
          <Skeleton variant="text" />
          <Skeleton variant="text" />
          <Skeleton variant="text" />
        </div>
      )}

      {/* Token addresses */}
      {tokenAddresses && (
        <div className="mt-2 space-y-1">
          <div className="text-text-muted flex items-center gap-1 text-xs">
            <span>A:</span>
            <AddressDisplay
              address={tokenAddresses[0]}
              fontSize={10}
              copyable
            />
          </div>
          <div className="text-text-muted flex items-center gap-1 text-xs">
            <span>B:</span>
            <AddressDisplay
              address={tokenAddresses[1]}
              fontSize={10}
              copyable
            />
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-3 flex gap-2 border-t border-[var(--glass-border)] pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `swap?tokenIn=${tokenSymbols[0]}&tokenOut=${tokenSymbols[1]}&version=v2`,
            );
          }}
        >
          {"\u21C4"} Swap
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `liquidity?token0=${tokenSymbols[0]}&token1=${tokenSymbols[1]}&version=v2`,
            );
          }}
        >
          + Add Liquidity
        </Button>
      </div>
    </Glass>
  );
}
